import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useColors } from "../constants/Colors";
import { QRCodeSVG } from "qrcode.react";
import { FaCopy, FaCheck } from "react-icons/fa";
import { IoClose, IoShareSocial } from "react-icons/io5";
import { trpcReact } from "../trpc_client";

interface AddMembersModalProps {
  isOpen: boolean;
  onClose: () => void;
  flatId: number;
  flatName: string;
}

const JOIN_URL = "https://flateroo.com/app/joinflat/";

const AddMembersModal: React.FC<AddMembersModalProps> = ({
  isOpen,
  onClose,
  flatId,
  flatName,
}) => {
  const { t } = useTranslation();
  const colors = useColors();
  const [copied, setCopied] = useState(false);

  // Fetch join code for the flat
  const { data: joinCodeData, isLoading } = trpcReact.getJoinCode.useQuery(
    { flat_id: flatId },
    { enabled: isOpen && flatId > 0 },
  );

  const joinCode = joinCodeData?.join_code || "";
  const joinLink = `${JOIN_URL}${joinCode}/${flatId}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(joinLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleShare = async () => {
    const shareMessage =
      t("flats_tab.add_flat.share_to_join_message") ||
      "Join my flat on Flateroo!";

    if (navigator.share) {
      try {
        await navigator.share({
          title: t("share_flat") || "Share Flat",
          text: `${shareMessage}\n\n${joinLink}`,
        });
      } catch (err) {
        // User cancelled or error - fallback to copy
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
        style={{ backgroundColor: colors.containerNeutralSolidSurface }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: colors.containerNeutralLightMedium }}
        >
          <h2
            className="text-xl font-semibold"
            style={{ color: colors.textNeutralDefault }}
          >
            {t("flats_tab.add_flat.flat_qr_code") || "Add Flat Members"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:opacity-70 transition-opacity"
            style={{ color: colors.textNeutralMuted }}
          >
            <IoClose size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <p
            className="text-sm mb-6 text-center"
            style={{ color: colors.textNeutralMuted }}
          >
            {t("flats_tab.add_flat.qr_code_hint") ||
              "Share this QR code or link with your flatmates so they can join your flat."}
          </p>

          {isLoading ? (
            <div
              className="flex items-center justify-center py-12"
              style={{ color: colors.textNeutralMuted }}
            >
              {t("loading") || "Loading..."}
            </div>
          ) : joinCode ? (
            <div className="flex flex-col items-center">
              {/* QR Code */}
              <div
                className="p-6 rounded-2xl mb-6"
                style={{ backgroundColor: colors.containerNeutralLightLowest }}
              >
                <QRCodeSVG
                  value={joinLink}
                  size={180}
                  level="M"
                  includeMargin={false}
                  fgColor={colors.textNeutralDefault}
                  bgColor="transparent"
                />
              </div>

              {/* Flat name */}
              <p
                className="text-lg font-medium mb-4"
                style={{ color: colors.textNeutralDefault }}
              >
                {flatName}
              </p>

              {/* Action buttons */}
              <div className="flex flex-wrap justify-center gap-3 w-full">
                {/* Copy link button */}
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all hover:opacity-80"
                  style={{
                    backgroundColor: colors.containerNeutralLightMedium,
                    color: colors.textNeutralDefault,
                  }}
                >
                  {copied ? <FaCheck size={16} /> : <FaCopy size={16} />}
                  <span>
                    {copied
                      ? t("copied") || "Copied!"
                      : t("copy_link") || "Copy Link"}
                  </span>
                </button>

                {/* Share button */}
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 px-5 py-3 rounded-full font-medium transition-all hover:opacity-80"
                  style={{
                    backgroundColor: colors.accentColor,
                    color: colors.textNeutralOnPrimary,
                  }}
                >
                  <IoShareSocial size={18} />
                  <span>
                    {t("flats_tab.add_flat.share_flat_link") || "Share Link"}
                  </span>
                </button>
              </div>
            </div>
          ) : (
            <div
              className="text-center py-12"
              style={{ color: colors.textNeutralMuted }}
            >
              {t("error_loading_join_code") || "Could not load join code"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddMembersModal;
